import type { DisplayAdsContentTargetingInput } from '@tunein/display-ads';
import { isProgram, isStation, isTopic } from '@tunein/web-common';

/**
 * Content key-values for the guide item a page is about, derived from the profile the app already
 * has in redux.
 *
 * Why this exists: contextual line items match on `class`, `genre_id`, `stationId` and `ListingId`.
 * The display-ads library only learns those from the player's now-playing metadata, so a page
 * carries none of them until the user actually tunes — and most pageviews never do. The legacy ad
 * path does not have that gap because it reads the same keys off the browsed profile whenever
 * now-playing is empty; this reproduces that fallback so both arms send the same signals.
 *
 * Precedence is handled by the library: these are outranked by now-playing metadata, so browsing
 * station B while station A plays still targets A.
 */

/** The subset of a guide item this derivation reads. Loose by design — profiles are untyped here. */
type ProfileLike = {
  guideId?: string;
  actions?: { play?: Record<string, unknown> };
  metadata?: { properties?: { classification?: Record<string, unknown> } };
};

/**
 * Mint resolves each classification key in three steps, and the order matters: the value attached
 * to the play action is more specific than the profile's own classification.
 */
function resolve(profile: ProfileLike, playKey: string, profileKey: string) {
  return (
    profile.actions?.play?.[playKey] ?? profile.metadata?.properties?.classification?.[profileKey]
  );
}

function toTargetingBool(value: unknown) {
  return value === undefined || value === null ? undefined : String(Boolean(value));
}

/**
 * `ListingId` is the most specific id available, as in the legacy path: topic beats program beats
 * station.
 */
function buildIds(guideId?: string) {
  if (!guideId) {
    return {};
  }

  const ids: Record<string, string> = {};

  if (isTopic(guideId)) {
    ids.topicId = guideId;
  } else if (isProgram(guideId)) {
    ids.programId = guideId;
  } else if (isStation(guideId)) {
    ids.stationId = guideId;
  }

  const listingId = ids.topicId || ids.programId || ids.stationId;
  if (listingId) {
    ids.ListingId = listingId;
  }

  return ids;
}

/**
 * Returns `{}` when there is no profile to describe — which clears any keys a previous page set,
 * rather than leaving the last station's targeting attached to an unrelated screen.
 */
export function buildContentTargeting(profile?: ProfileLike): DisplayAdsContentTargetingInput {
  if (!profile) {
    return {};
  }

  const targeting: DisplayAdsContentTargetingInput = buildIds(profile.guideId);

  const contentClass = resolve(profile, 'contentType', 'rootGenreClassification');
  if (contentClass) {
    targeting.class = String(contentClass);
  }

  const genreId = resolve(profile, 'genreId', 'primaryGenreId');
  if (genreId) {
    targeting.genre_id = String(genreId);
  }

  const bandId = profile.metadata?.properties?.classification?.bandId;
  if (bandId) {
    targeting.BandId = String(bandId);
  }

  // The library already sends these as 'false' defaults, so only override when the profile has an
  // opinion — otherwise an absent flag would look like an explicit `false` from the page.
  const flags = {
    is_family: resolve(profile, 'isFamilyContent', 'isFamilyContent'),
    is_mature: resolve(profile, 'isMatureContent', 'isMatureContent'),
    is_event: resolve(profile, 'isEvent', 'isEvent'),
    is_ondemand: resolve(profile, 'isOnDemand', 'isOnDemand'),
  };

  for (const [key, value] of Object.entries(flags)) {
    const normalized = toTargetingBool(value);
    if (normalized !== undefined) {
      targeting[key] = normalized;
    }
  }

  return targeting;
}
